import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setTabsMinimized } from "../actions/window";
const defaultValue = {
    isFocused: true,
    isHidpi: false,
    menuIsCollapsed: false,
    setMenuIsCollapsed: () => { },
    getModifiers: () => ({ alt: false, ctrl: false, shift: false }),
};
const WindowContext = createContext(defaultValue);
export const WindowProvider = ({ children }) => {
    const dispatch = useDispatch();
    const tabsMinimized = useSelector((state) => state.settings.window.tabsMinimized);
    const [isHidpi, setIsHidpi] = useState(() => (global.screen?.width ?? 0) > 1920);
    const [isFocused, setIsFocused] = useState(true);
    const modifiersRef = useRef({
        alt: false,
        ctrl: false,
        shift: false,
    });
    const tabsMinimizedRef = useRef(tabsMinimized);
    tabsMinimizedRef.current = tabsMinimized;
    const getModifiers = useCallback(() => modifiersRef.current, []);
    const updateModifiers = useCallback((event) => {
        const current = modifiersRef.current;
        if (event.altKey !== current.alt ||
            event.ctrlKey !== current.ctrl ||
            event.shiftKey !== current.shift) {
            modifiersRef.current = {
                alt: event.altKey,
                ctrl: event.ctrlKey,
                shift: event.shiftKey,
            };
        }
    }, []);
    const setMenuIsCollapsed = useCallback((value) => {
        const newValue = typeof value === "function" ? value(tabsMinimizedRef.current) : value;
        dispatch(setTabsMinimized(newValue));
    }, [dispatch]);
    // Window focus/blur and resize listeners
    useEffect(() => {
        const handleFocus = () => {
            if (process.env.DEBUG_REACT_RENDERS !== "true") {
                setIsFocused(true);
            }
        };
        const handleBlur = () => {
            if (process.env.DEBUG_REACT_RENDERS !== "true") {
                setIsFocused(false);
            }
        };
        const handleResize = () => {
            const newIsHidpi = (global.screen?.width ?? 0) > 1920;
            setIsHidpi((prev) => (prev !== newIsHidpi ? newIsHidpi : prev));
        };
        window.addEventListener("focus", handleFocus);
        window.addEventListener("blur", handleBlur);
        window.addEventListener("resize", handleResize);
        window.addEventListener("keydown", updateModifiers);
        window.addEventListener("keyup", updateModifiers);
        return () => {
            window.removeEventListener("focus", handleFocus);
            window.removeEventListener("blur", handleBlur);
            window.removeEventListener("resize", handleResize);
            window.removeEventListener("keydown", updateModifiers);
            window.removeEventListener("keyup", updateModifiers);
        };
    }, [updateModifiers]);
    const contextValue = useMemo(() => ({
        isFocused,
        isHidpi,
        menuIsCollapsed: tabsMinimized,
        setMenuIsCollapsed,
        getModifiers,
    }), [isFocused, isHidpi, tabsMinimized, setMenuIsCollapsed, getModifiers]);
    return (React.createElement(WindowContext.Provider, { value: contextValue }, children));
};
export const WindowConsumer = WindowContext.Consumer;
export function useWindowContext() {
    return useContext(WindowContext);
}
